import type { GeofencesByBoundariesResult, ManagedGeofence } from './types'

export interface GeofencesByBoundariesApiItem {
  id: string
  name: string
  companyId: string
  coordinates: [number, number][]
}

const parseNumber = (value: string): number | undefined => {
  if (!value.trim()) return undefined
  const number = Number(value)
  return Number.isFinite(number) ? number : undefined
}

const boundsFromCoordinates = (coordinates: [number, number][]) => {
  const latitudes = coordinates.map(([latitude]) => latitude)
  const longitudes = coordinates.map(([, longitude]) => longitude)
  return {
    north: Math.max(...latitudes),
    south: Math.min(...latitudes),
    east: Math.max(...longitudes),
    west: Math.min(...longitudes),
  }
}

const managedGeofenceFromBoundaryItem = (
  item: GeofencesByBoundariesApiItem,
  fallbackCompanyId: number
): ManagedGeofence | undefined => {
  const id = parseNumber(item.id)
  const { coordinates } = item
  if (
    id === undefined ||
    coordinates.length < 3 ||
    !coordinates.every(([latitude, longitude]) => Number.isFinite(latitude) && Number.isFinite(longitude))
  ) {
    return undefined
  }

  return {
    id,
    name: item.name,
    companyId: parseNumber(item.companyId) ?? fallbackCompanyId,
    version: 0,
    zorder: 1,
    visible: true,
    complexityTypeId: null,
    categoryIds: [],
    bounds: boundsFromCoordinates(coordinates),
    coordinates,
  }
}

export const boundaryResultFromResponse = (
  items: GeofencesByBoundariesApiItem[],
  companyId: number
): GeofencesByBoundariesResult => {
  const ids = [...new Set(items.map((item) => parseNumber(item.id)).filter((id): id is number => id !== undefined))]
  const geofences = items
    .map((item) => managedGeofenceFromBoundaryItem(item, companyId))
    .filter((geofence): geofence is ManagedGeofence => !!geofence)

  return { ids, geofences }
}
